import axiosInstance from "../interceptors";

// Reports & Analytics data sources for /admin/reports.
//
// Everything on that screen used to be module-level constants (2847 applications,
// LinkedIn 892, six departments of hiring goals…). These are the real endpoints
// behind each widget. Several legitimately return nothing for a young tenant —
// callers must render an empty state rather than fabricate numbers.
//
// All of these live in common-service, exposed through the gateway under /common
// (StripPrefix=1). tenantId is validated against the JWT claim server-side, so a
// mismatch is rejected; we send it because the endpoints accept it explicitly.

const ANALYTICS = "/common/analytics";
const DASHBOARD = `${ANALYTICS}/dashboard`;

const getTenantId = () => localStorage.getItem("tenantId") || null;

const withTenant = (params = {}) => {
  const tenantId = getTenantId();
  return tenantId ? { tenantId, ...params } : { ...params };
};

/** KPI tiles — open jobs, active candidates, interviews/wk, avg AI score. */
export const getKpis = (period = "30d", opts = {}) =>
  axiosInstance.get(`${DASHBOARD}/kpis`, { ...opts, params: withTenant({ period }) });

/**
 * Application funnel. Uses the dashboard pipeline rather than /analytics/funnel:
 * the latter reads amplehire_candidate_status_t, which is empty on tenants that
 * haven't run the status workflow, while this one is derived from real invites.
 */
export const getPipeline = (period = "30d", opts = {}) =>
  axiosInstance.get(`${DASHBOARD}/pipeline`, { ...opts, params: withTenant({ period }) });

/** Stage-code funnel (conversion + avg days per stage). Often all-zero. */
export const getStageFunnel = (jobId = null, opts = {}) =>
  axiosInstance.get(`${ANALYTICS}/funnel`, {
    ...opts,
    params: withTenant(jobId ? { jobId } : {}),
  });

/**
 * Source attribution by channel. Note this reads amplehire_job_application_t,
 * i.e. career-page applications only — candidates added by resume upload or
 * pool import never appear here.
 */
export const getSourceAttribution = (period = "30d", opts = {}) =>
  axiosInstance.get(`${ANALYTICS}/source-attribution`, { ...opts, params: { period } });

/** Hiring goals for the current quarter. Empty until goals are created. */
export const getHiringGoals = (period = "30d", opts = {}) =>
  axiosInstance.get(`${DASHBOARD}/hiring-goals`, { ...opts, params: withTenant({ period }) });

/** Saved reports + shared templates for this tenant. */
export const getSavedReports = (opts = {}) =>
  axiosInstance.get(`${ANALYTICS}/reports`, { ...opts, params: withTenant() });

/** Metric keys the query engine supports (drives the builder's Metric list). */
export const getMetricList = (opts = {}) =>
  axiosInstance.get(`${ANALYTICS}/metrics/list`, opts);

/** Time series for one or more metric keys. */
export const queryMetrics = (
  metrics,
  { from = "2026-01", to = "2026-12", granularity = "MONTH" } = {},
  opts = {},
) =>
  axiosInstance.post(
    `${ANALYTICS}/query`,
    {
      tenantId: getTenantId() ? Number(getTenantId()) : null,
      metrics,
      dateRange: { from, to },
      timeGranularity: granularity,
    },
    opts,
  );

/** Report-builder preview — same engine as a saved report's run. */
export const previewReport = (metrics, opts = {}) =>
  axiosInstance.post(
    `${ANALYTICS}/reports/preview`,
    { tenantId: getTenantId() ? Number(getTenantId()) : null, metrics },
    opts,
  );

/**
 * UI label → engine metric key. The builder shows friendly names; the engine
 * only knows the keys from /metrics/list.
 */
export const METRIC_KEY_BY_LABEL = {
  "Time to Hire": "time_to_hire",
  Applications: "applications_count",
  Interviews: "pipeline_velocity",
  Offers: "offer_acceptance_rate",
  "Source Attribution": "pipeline_by_source",
  Diversity: "diversity_pipeline",
};
